/**
 * Display formatting.
 *
 * Money arrives from the API as integer cents and is formatted here. It is
 * never divided by 100 inline in a component — one place to be right about
 * rounding, and one place to change if a brand ever ships outside USD.
 */

export function formatUsd(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const body = `$${Math.floor(abs / 100).toLocaleString('en-US')}.${String(abs % 100).padStart(2, '0')}`;
  return negative ? `−${body}` : body;
}

/**
 * Micro-dollars, as spent on one ticket.
 *
 * A different unit from `formatUsd` and deliberately not folded into it: money
 * moved to a CUSTOMER is cents, and rounding to cents is right. Money spent on
 * a MODEL lives three decimal places below that — a ticket costs fractions of a
 * cent, and showing $0.00 would make the whole measurement pointless.
 */
export function formatMicroUsd(microUsd: number | null | undefined): string {
  if (microUsd === null || microUsd === undefined) return '—';
  const dollars = microUsd / 1_000_000;
  if (Math.abs(dollars) < 0.01) return `$${dollars.toFixed(4)}`;
  if (Math.abs(dollars) < 1) return `$${dollars.toFixed(3)}`;
  return `$${dollars.toFixed(2)}`;
}

export function formatPercent(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

/** "2h ago", "3d ago". Absolute dates go in a title attribute alongside. */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const seconds = Math.round((now.getTime() - then) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** `12 Oct 2026, 19:10` — a moment to the minute, in the viewer's local time. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * A draft as a person should read it: the billing-link placeholder named for
 * what it is. The real link is only fetched at the moment a reply is sent
 * (reactivation, 2026-10-05), so no screen before that can show it. Display
 * only — anything sent or edited keeps the placeholder itself.
 */
export function withBillingLinkStandIn(text: string): string {
  return text
    .split('{{billing_update_link}}')
    .join('[secure billing link — added when sent]')
    // The temporary password the same way (2026-10-28): set and added at sending.
    .split('{{temporary_password}}')
    .join('[temporary password — set and added when sent]');
}
